import { type MessageDirect, messageDirectSchema } from "../contract";

/**
 * Le client du flux du direct (lot Q1) : lit `text/event-stream` par `fetch`, en flux, avec des en-tetes libres.
 *
 * Pas d'`EventSource` : il ne sait pas envoyer d'en-tete, la cle d'overlay devrait donc passer dans l'adresse,
 * et le nginx du template journalise les adresses completes. Ici la cle part en `Authorization: Cle <cle>`.
 *
 * - **Lecture** : les morceaux sont recolles (un message peut arriver en dix morceaux, ou dix messages en un),
 *   les commentaires (`: ouvert`, `: battement`) sont ignores, un message hors du schema du bus est ignore
 *   sans couper le flux. `event: reprise` n'est pas un message du bus : le serveur dit que sa memoire a change
 *   (il a redemarre, ou le client a manque plus que sa garde), `surReprise` est appelee ;
 * - **Reprise** : a chaque reconnexion, le dernier `id` recu part en `Last-Event-ID`. Les delais vont de 2 s a
 *   4 s, 8 s... jusqu'a 30 s, et reviennent a 2 s apres 60 s de connexion stable. Le serveur lit la session de la
 *   regie une fois par ouverture : jamais moins de 2 s entre deux, qu'une coupure qui boucle ne martele pas la base ;
 * - **Refus** : un 401 ou un 403 arrete les reprises et le dit (`coupe`, raison `refus`) ; reessayer ne
 *   changerait rien. Un 429 (trop de flux ouverts) reprend apres 30 s ;
 * - **Trou** : si le premier numero recu apres une coupure n'est pas le suivant du dernier (le serveur n'en garde
 *   que 200), le client le sait et appelle `surReprise` comme pour une reprise.
 *
 * Rien n'est journalise ici, et l'adresse et les en-tetes (la cle) ne sortent jamais dans un etat ni un message.
 */

export type RaisonCoupure = "fin" | "erreur" | "limite" | "refus";

export type EtatFlux =
  | { genre: "connexion" }
  | { genre: "connecte"; depuis: number }
  | {
      genre: "coupe";
      depuis: number;
      tentatives: number;
      raison: RaisonCoupure;
      /** Le statut HTTP quand la coupure vient d'une reponse du serveur. */
      statut?: number;
    };

/** Le premier delai de reprise, et le plancher apres une connexion stable. */
export const DELAI_MIN_MS = 2_000;
/** Le plafond du delai de reprise. */
export const DELAI_MAX_MS = 30_000;
/** Une connexion tenue aussi longtemps est « stable » : le prochain delai revient a `DELAI_MIN_MS`. */
export const CONNEXION_STABLE_MS = 60_000;
/** L'attente apres un 429. */
export const DELAI_LIMITE_MS = 30_000;
/** L'attente apres un 401 ou un 403 quand `surRefus` vaut `relance-lente`. */
export const DELAI_REFUS_LENT_MS = 60_000;
/** Sans un octet pendant ce temps (trois battements de 15 s), la connexion est tenue pour morte. */
export const SILENCE_MAX_MS = 45_000;

export type OptionsFlux = {
  url: string;
  entetes?: Record<string, string> | undefined;
  surMessage: (message: MessageDirect) => void;
  surEtat?: ((etat: EtatFlux) => void) | undefined;
  /**
   * Que faire d'un 401 ou d'un 403 : `arret` (par defaut, pour un overlay : sa cle est fausse, reessayer ne
   * changerait rien) ou `relance-lente` (la regie : un 401 peut venir d'une base en panne, qui empeche de lire la session, et Nicolas
   * ne doit pas rester coupe). Dans les deux cas l'etat dit `coupe` / `refus`.
   */
  surRefus?: "arret" | "relance-lente" | undefined;
  /** Le serveur a change de memoire (ou le client a manque des messages) : relire l'etat. */
  surReprise?: (() => void) | undefined;
  fetchImpl?: typeof fetch | undefined;
  /** L'horloge, pour les tests. */
  maintenant?: (() => number) | undefined;
};

type Evenement = { id: string | null; genre: string; data: string };

/** Lit les lignes SSE au fil des morceaux et rend les evenements termines (une ligne vide les clot). */
export function lecteurSse() {
  let tampon = "";
  let id: string | null = null;
  let genre = "";
  let data: string[] = [];

  function ligne(texte: string, sortie: Evenement[]): void {
    if (texte === "") {
      if (data.length > 0 || genre !== "") {
        sortie.push({ id, genre, data: data.join("\n") });
      }
      genre = "";
      data = [];
      return;
    }
    if (texte.startsWith(":")) return;
    const deuxPoints = texte.indexOf(":");
    const champ = deuxPoints === -1 ? texte : texte.slice(0, deuxPoints);
    let valeur = deuxPoints === -1 ? "" : texte.slice(deuxPoints + 1);
    if (valeur.startsWith(" ")) valeur = valeur.slice(1);
    if (champ === "id") {
      if (!valeur.includes("\0")) id = valeur;
    } else if (champ === "event") genre = valeur;
    else if (champ === "data") data.push(valeur);
  }

  return {
    pousser(morceau: string): Evenement[] {
      tampon += morceau;
      const sortie: Evenement[] = [];
      for (;;) {
        const fin = /\r\n|\n|\r/.exec(tampon);
        if (fin === null) break;
        // Un `\r` en bout de tampon peut etre le debut d'un `\r\n` : attendre le morceau suivant.
        if (fin[0] === "\r" && fin.index === tampon.length - 1) break;
        ligne(tampon.slice(0, fin.index), sortie);
        tampon = tampon.slice(fin.index + fin[0].length);
      }
      return sortie;
    },
  };
}

/** `<epoque>.<numero>` en ses deux morceaux, ou `null`. */
function decouperId(id: string): { epoque: string; numero: number } | null {
  const trouve = /^(.+)\.(\d{1,15})$/.exec(id);
  return trouve === null ? null : { epoque: trouve[1] ?? "", numero: Number(trouve[2]) };
}

export function ouvrirFlux(options: OptionsFlux): { fermer(): void } {
  const lireFetch = options.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const maintenant = options.maintenant ?? Date.now;
  /** Le controleur de la connexion en cours : un par essai, pour que le chien de garde coupe l'un sans l'autre. */
  let courant: AbortController | null = null;
  /** Le chien de garde de la connexion en cours. */
  let chien: ReturnType<typeof setTimeout> | undefined;
  let ferme = false;
  let minuteur: ReturnType<typeof setTimeout> | undefined;
  /** Le dernier `id` recu, renvoye en `Last-Event-ID`. */
  let dernierId: string | null = null;
  /** Les echecs de suite sans connexion stable : le delai double avec eux. */
  let echecs = 0;

  function dire(etat: EtatFlux): void {
    if (!ferme) options.surEtat?.(etat);
  }

  function attendre(ms: number): Promise<void> {
    return new Promise((resolve) => {
      minuteur = setTimeout(resolve, ms);
    });
  }

  /** Un evenement lu : `reprise`, un message du bus, ou rien (hors schema). */
  function traiter(evenement: Evenement): void {
    if (evenement.genre === "reprise") {
      // La memoire du serveur a change : l'ancien numero ne dit plus rien, il ne repart pas en Last-Event-ID.
      dernierId = null;
      options.surReprise?.();
      return;
    }
    if (evenement.data === "") return;
    let brut: unknown;
    try {
      brut = JSON.parse(evenement.data);
    } catch {
      brut = undefined;
    }
    const lu = messageDirectSchema.safeParse(brut);
    if (evenement.id !== null) {
      const avant = dernierId === null ? null : decouperId(dernierId);
      const apres = decouperId(evenement.id);
      // Un trou dans les numeros de la meme epoque : le serveur n'a pas tout garde.
      if (
        avant !== null &&
        apres !== null &&
        avant.epoque === apres.epoque &&
        apres.numero > avant.numero + 1
      ) {
        options.surReprise?.();
      }
      dernierId = evenement.id;
    }
    if (lu.success) options.surMessage(lu.data);
  }

  async function lire(
    corps: ReadableStream<Uint8Array>,
    signal: AbortSignal,
    octetRecu: () => void,
  ): Promise<void> {
    const lecteur = corps.getReader();
    // Un fetch qui ignorerait le signal laisserait la lecture pendante : on l'annule nous-memes.
    signal.addEventListener("abort", () => void lecteur.cancel().catch(() => undefined), {
      once: true,
    });
    const decodeur = new TextDecoder();
    const sse = lecteurSse();
    try {
      for (;;) {
        const { done, value } = await lecteur.read();
        if (done || ferme) return;
        octetRecu();
        for (const evenement of sse.pousser(decodeur.decode(value, { stream: true }))) {
          if (ferme) return;
          traiter(evenement);
        }
      }
    } finally {
      try {
        lecteur.releaseLock();
      } catch {
        // Une lecture encore en attente refuse de rendre le verrou : le flux est abandonne de toute facon.
      }
    }
  }

  async function boucle(): Promise<void> {
    while (!ferme) {
      dire({ genre: "connexion" });
      let delai: number;
      let raison: RaisonCoupure = "erreur";
      let statut: number | undefined;
      let ouvertA: number | null = null;
      let delaiFixe: number | null = null;
      const ctl = new AbortController();
      courant = ctl;
      try {
        const reponse = await lireFetch(options.url, {
          headers: {
            accept: "text/event-stream",
            ...options.entetes,
            ...(dernierId === null ? {} : { "last-event-id": dernierId }),
          },
          cache: "no-store",
          signal: ctl.signal,
        });
        if (ferme) return;
        if (reponse.status === 401 || reponse.status === 403) {
          void reponse.body?.cancel().catch(() => undefined);
          if (options.surRefus !== "relance-lente") {
            dire({
              genre: "coupe",
              depuis: maintenant(),
              tentatives: echecs,
              raison: "refus",
              statut: reponse.status,
            });
            return;
          }
          raison = "refus";
          statut = reponse.status;
          delaiFixe = DELAI_REFUS_LENT_MS;
        } else if (reponse.status === 429) {
          void reponse.body?.cancel().catch(() => undefined);
          raison = "limite";
          statut = 429;
        } else if (reponse.ok && reponse.body !== null) {
          ouvertA = maintenant();
          dire({ genre: "connecte", depuis: ouvertA });
          const armer = () => {
            clearTimeout(chien);
            chien = setTimeout(() => ctl.abort(), SILENCE_MAX_MS);
          };
          armer();
          await lire(reponse.body, ctl.signal, armer);
          raison = ctl.signal.aborted ? "erreur" : "fin";
        } else {
          void reponse.body?.cancel().catch(() => undefined);
          statut = reponse.status;
        }
      } catch {
        if (ferme) return;
        raison = "erreur";
      }
      clearTimeout(chien);
      if (ferme) return;

      if (ouvertA !== null && maintenant() - ouvertA >= CONNEXION_STABLE_MS) echecs = 0;
      if (delaiFixe !== null) {
        delai = delaiFixe;
      } else if (raison === "limite") {
        delai = DELAI_LIMITE_MS;
      } else {
        delai = Math.min(DELAI_MIN_MS * 2 ** echecs, DELAI_MAX_MS);
      }
      echecs += 1;
      dire({
        genre: "coupe",
        depuis: maintenant(),
        tentatives: echecs,
        raison,
        ...(statut === undefined ? {} : { statut }),
      });
      await attendre(delai);
    }
  }

  void boucle();

  return {
    fermer() {
      if (ferme) return;
      ferme = true;
      if (minuteur !== undefined) clearTimeout(minuteur);
      clearTimeout(chien);
      courant?.abort();
    },
  };
}
